<template>
  <main class="quick-config-page">
    <section class="config-grid">
      <div class="soft-card info-card">
        <div class="info-label">API Key</div>
        <div class="info-value">{{ config.apiKey || '-' }}</div>
        <el-button size="small" type="text" icon="el-icon-document-copy" @click="copyText(config.apiKey)">复制</el-button>
      </div>
      <div class="soft-card info-card">
        <div class="info-label">OpenAI 协议地址</div>
        <div class="info-value">{{ openAiEndpoint || '-' }}</div>
        <el-button size="small" type="text" icon="el-icon-document-copy" @click="copyText(openAiEndpoint)">复制</el-button>
      </div>
      <div class="soft-card info-card">
        <div class="info-label">可用模型</div>
        <div class="info-value">{{ modelsText || '-' }}</div>
        <el-button size="small" type="text" @click="selectedTool = tools[0]">查看配置</el-button>
      </div>
    </section>

    <section class="soft-card tools-card">
      <div class="section-title compact">
        <h2>常用工具</h2>
      </div>
      <div class="tool-grid">
        <button
          v-for="tool in tools"
          :key="tool.name"
          type="button"
          class="tool-card"
          :class="{ active: selectedTool && selectedTool.name === tool.name }"
          @click="selectedTool = tool"
        >
          <span class="tool-icon" :style="{ background: tool.gradient }">
            <span>{{ tool.abbr }}</span>
          </span>
          <span class="tool-meta">
            <strong>{{ tool.name }}</strong>
            <small>{{ tool.summary }}</small>
          </span>
        </button>
      </div>

      <div v-if="selectedTool" class="tool-detail">
        <div class="tool-detail-head">
          <span class="tool-icon large" :style="{ background: selectedTool.gradient }">
            <span>{{ selectedTool.abbr }}</span>
          </span>
          <div>
            <h3>{{ selectedTool.name }}</h3>
            <p>{{ selectedTool.description }}</p>
          </div>
        </div>
        <div class="section-block">
          <div class="block-title-row">
            <div class="block-title">配置文件位置</div>
            <el-button size="small" type="text" icon="el-icon-document-copy" @click="copyText(selectedTool.command)">复制</el-button>
          </div>
          <div class="code-block">{{ selectedTool.command }}</div>
        </div>
        <div class="section-block" v-if="selectedTool.usage">
          <div class="block-title-row">
            <div class="block-title">使用说明</div>
            <el-button size="small" type="text" icon="el-icon-document-copy" @click="copyText(selectedTool.usage)">复制</el-button>
          </div>
          <div class="code-block">{{ selectedTool.usage }}</div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: 'QuickConfigPage',
  data() {
    return {
      config: {
        apiKey: '',
        baseUrl: ''
      },
      models: [],
      selectedTool: null
    }
  },
  computed: {
    modelsText() {
      return this.models.map(m => m.modelName).join('、') || ''
    },
    openAiEndpoint() {
      return (this.config.baseUrl || '').replace(/\/+$/, '') + '/chat/completions'
    },
    openAiHost() {
      return (this.config.baseUrl || '').replace(/\/v1$/, '')
    },
    tools() {
      return [
        {
          name: 'OpenCode',
          abbr: 'OC',
          summary: '开源 AI 编程助手 CLI',
          gradient: 'linear-gradient(135deg, #6366f1, #06b6d4)',
          description: 'OpenCode 是开源的终端 AI 编程助手，通过 OpenAI 兼容协议接入当前网关。',
          command: '全局配置位置：~/.config/opencode/opencode.json\n项目配置位置：./opencode.json 或 .opencode/opencode.json\n\nMac/Linux 创建命令：mkdir -p ~/.config/opencode',
          usage: '将下面内容写入 opencode.json：\n\n' + this.openCodeSettings + '\n\n填写说明：\n- baseURL 使用网关的 OpenAI 协议地址（含 /v1 结尾，如 http://你的服务器:8092/v1）。\n- apiKey 使用当前页面顶部的 API Key。\n- model 和 small_model 中的模型 ID 必须是网关里已分配给当前账号的模型名称。\n- 保存后重启 OpenCode 即可生效。'
        },
        {
          name: 'WorkBuddy',
          abbr: 'WB',
          summary: '腾讯桌面 AI 智能体',
          gradient: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
          description: 'WorkBuddy 通过配置文件 models.json 接入自定义模型，使用 OpenAI 兼容协议把请求转发到当前网关。',
          command: '在用户目录下创建 .workbuddy 文件夹并新建 models.json：\nMac/Linux：~/.workbuddy/models.json\nWindows：C:\\Users\\你的用户名\\.workbuddy\\models.json\n\n创建文件夹命令：\nMac/Linux：mkdir -p ~/.workbuddy\nWindows：mkdir %USERPROFILE%\\.workbuddy',
          usage: '将下面内容写入 models.json：\n\n' + this.workbuddySettings + '\n\n填写说明：\n- url 使用网关的 OpenAI 协议地址（完整的 /v1/chat/completions）。\n- apiKey 使用当前页面顶部的 API Key。\n- id 必须是网关里已分配给当前账号的模型名称。\n- 如需隐藏内置模型，可加上 "availableModels": ["deepseek-v4-flash"]。\n- 保存后必须重启 WorkBuddy 才能生效，然后在模型列表里选择已分配的模型。'
        },
        {
          name: 'Cline',
          abbr: 'CL',
          summary: 'VS Code AI 编程助手',
          gradient: 'linear-gradient(135deg, #f472b6, #8b5cf6)',
          description: 'Cline 是 VS Code 的 AI 编程助手插件，通过 OpenAI 兼容 Provider 接入当前网关。',
          command: '1. 在 VS Code 插件市场安装 Cline\n2. 打开 Cline 面板，点击右上角设置（JSON）\n3. API Provider 选择 "OpenAI Compatible"\n4. 按下方配置项填写后保存',
          usage: this.clineUsage
        },
        {
          name: 'cURL 调用',
          abbr: 'CU',
          summary: '标准 OpenAI 协议命令行调用',
          gradient: 'linear-gradient(135deg, #10b981, #0ea5e9)',
          description: '用标准 OpenAI 兼容的 curl 命令直接调用网关，适合命令行快速测试联调。',
          command: '在任意终端执行下方 curl 命令即可测试网关连通性。',
          usage: this.curlUsage
        }
      ]
    },
    openCodeSettings() {
      const modelNames = this.models.map(m => m.modelName)
      const primary = modelNames[0] || 'deepseek-v4-flash'
      const map = {}
      modelNames.forEach(name => {
        map[name] = { id: name, name }
      })
      if (!map[primary]) {
        map[primary] = { id: primary, name: primary }
      }
      return JSON.stringify({
        $schema: 'https://opencode.ai/config.json',
        permission: {
          edit: 'allow',
          bash: 'allow',
          webfetch: 'allow'
        },
        provider: {
          'ai-package': {
            npm: '@ai-sdk/openai-compatible',
            name: 'AI Package',
            options: {
              baseURL: this.config.baseUrl,
              apiKey: this.config.apiKey
            },
            models: map
          }
        },
        model: 'ai-package/' + primary,
        small_model: 'ai-package/' + primary
      }, null, 2)
    },
    workbuddySettings() {
      const models = this.models.length
        ? this.models.map(m => ({
            id: m.modelName,
            name: m.modelName,
            vendor: 'AI Package',
            apiKey: this.config.apiKey,
            maxInputTokens: 128000,
            maxOutputTokens: 8192,
            url: this.config.baseUrl + '/chat/completions',
            supportsToolCall: true,
            supportsImages: true
          }))
        : [{
            id: 'deepseek-v4-flash',
            name: 'deepseek-v4-flash',
            vendor: 'AI Package',
            apiKey: this.config.apiKey,
            maxInputTokens: 128000,
            maxOutputTokens: 8192,
            url: this.config.baseUrl + '/chat/completions',
            supportsToolCall: true,
            supportsImages: true
          }]
      return JSON.stringify({ models }, null, 2)
    },
    clineUsage() {
      return [
        'Base URL：' + this.config.baseUrl,
        'API Key：' + this.config.apiKey,
        'Model ID：' + (this.modelsText || 'deepseek-v4-flash'),
        '',
        '填写说明：',
        '- Base URL 使用网关的 OpenAI 协议地址（完整的 /v1 结尾）。',
        '- API Key 使用当前页面顶部的 API Key。',
        '- Model ID 必须是网关里已分配给当前账号的模型名称。',
        '- 保存后对话即可通过当前网关调用。'
      ].join('\n')
    },
    curlUsage() {
      const endpoint = this.openAiEndpoint || (this.config.baseUrl || 'http://你的服务器:8092/v1') + '/chat/completions'
      const model = this.modelsText ? this.modelsText.split('、')[0] : 'deepseek-v4-flash'
      const key = this.config.apiKey || 'sk-你的APIKey'
      return [
        '标准 OpenAI 协议，直接调用网关的 chat/completions 接口：',
        '',
        'curl ' + endpoint + ' \\',
        '  -H "Content-Type: application/json" \\',
        '  -H "Authorization: Bearer ' + key + '" \\',
        '  -d \'{"model": "' + model + '", "messages": [{"role": "user", "content": "你好"}], "stream": true}\'',
        '',
        '非流式调用（去掉 stream 即可）：',
        'curl ' + endpoint + ' \\',
        '  -H "Content-Type: application/json" \\',
        '  -H "Authorization: Bearer ' + key + '" \\',
        '  -d \'{"model": "' + model + '", "messages": [{"role": "user", "content": "你好"}]}\'',
        '',
        '查询可用模型：',
        'curl ' + (this.config.baseUrl || 'http://你的服务器:8092/v1') + '/models \\',
        '  -H "Authorization: Bearer ' + key + '"',
        '',
        '说明：',
        '- model 必须是网关已分配给当前账号的模型名称（' + (this.modelsText || 'deepseek-v4-flash') + '）。',
        '- Authorization 使用当前页面顶部的 API Key（Bearer 方式）。',
        '- Windows PowerShell 下建议将单引号改成转义双引号或使用 --data-raw 传 JSON。'
      ].join('\n')
    }
  },
  async mounted() {
    await this.fetchConfig()
  },
  methods: {
    async fetchConfig() {
      try {
        const [meRes, gwRes, modelRes] = await Promise.all([
          this.$http.get('/user/me'),
          this.$http.get('/user/gateway'),
          this.$http.get('/user/models')
        ])
        if (meRes.code === 200 && meRes.data) {
          this.config.apiKey = meRes.data.apiKey || ''
        }
        if (gwRes.code === 200 && gwRes.data) {
          this.config.baseUrl = gwRes.data.baseUrl || ''
        }
        if (modelRes.code === 200 && Array.isArray(modelRes.data)) {
          this.models = modelRes.data
        }
        this.selectedTool = this.tools[0]
      } catch (e) {
        this.$message.error('加载快速配置失败')
      }
    },
    copyText(text) {
      if (!text) return
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        this.$message.success('已复制')
      } catch (e) {
        this.$message.warning('复制失败')
      }
      document.body.removeChild(ta)
    }
  }
}
</script>

<style scoped>
.quick-config-page {
  padding: 4px 0 24px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.info-card {
  padding: 20px;
  border-radius: var(--radius);
  min-width: 0;
}

.info-label {
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 10px;
}

.info-value {
  min-height: 44px;
  font-size: 14px;
  line-height: 1.5;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-weight: 700;
  color: var(--text);
  word-break: break-all;
}

.tools-card {
  margin-top: 20px;
  padding: 20px;
  border-radius: var(--radius);
}

.section-title.compact h2 {
  margin: 0;
  font-size: 18px;
  color: var(--text);
}

.tool-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.tool-card {
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.045);
  padding: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.tool-card:hover,
.tool-card.active {
  border-color: rgba(50, 200, 245, 0.46);
  box-shadow: 0 12px 28px rgba(50, 150, 245, 0.18);
  transform: translateY(-1px);
}

.tool-icon {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  overflow: hidden;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.24);
}

.tool-icon.large {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  font-size: 16px;
}

.tool-meta {
  min-width: 0;
  display: grid;
  gap: 3px;
}

.tool-meta strong {
  font-size: 15px;
  color: var(--text);
}

.tool-meta small {
  font-size: 12px;
  color: var(--muted-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tool-detail {
  margin-top: 22px;
  border-top: 1px solid var(--line);
  padding-top: 20px;
}

.tool-detail-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.tool-detail h3 {
  margin: 0 0 6px;
  font-size: 20px;
  color: var(--text);
}

.tool-detail p {
  margin: 0;
  color: var(--muted);
  font-size: 14px;
}

.section-block {
  margin-top: 16px;
}

.block-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.block-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--muted);
}

.code-block {
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid var(--line-soft);
  border-radius: var(--radius);
  padding: 14px 16px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  color: #dbe8fb;
}

@media (max-width: 1100px) {
  .config-grid {
    grid-template-columns: 1fr;
  }
}
</style>